<template>
  <!-- ============================================================
       No.9 (B) 代行者権限付与：採択者による代行者の自己設定
       代行者ができるのは「採択者手続き」と「問合せ」の入力のみ（申請・報告本体は見えない）。
       有効期限は指定させない＝サーバが年度末（3/31）を自動付与する。
       ============================================================ -->
  <div id="MyAgent" class="wrap">
    <div class="formWrap">
      <h1 class="title">設定 - 代行者</h1>

      <div class="inputBox">
        <p class="lead mb-1">
          報告に必要な手続きを、代わりに入力してもらう方（代行者）を登録できます。
        </p>
        <ul class="notes mb-4">
          <li>代行者ができるのは「<span class="bold">採択者手続き</span>」と「<span class="bold">問合せ</span>」の入力のみです。</li>
          <li>申請内容・報告内容は代行者には表示されません。</li>
          <li>登録すると、代行者ご本人にパスワード設定のメールが届きます。</li>
          <li>ログインできる期限は<span class="bold">年度末（3月31日）まで</span>です。期限が切れたあと、同じ方をもう一度登録すると期限が延長されます。</li>
        </ul>

        <!-- 登録済みの代行者 -->
        <div class="bold mb-2">登録している代行者</div>
        <b-table striped hover
          table-class="myAgentTable"
          thead-class="tableHead"
          :items="agents"
          :fields="header"
          show-empty>
          <template v-slot:empty>
            <div class="flex flexCenter">代行者は登録されていません。</div>
          </template>
          <template v-slot:cell(no)="row">
            {{ row.index + 1 }}
          </template>
          <template v-slot:cell(status)="row">
            <span :class="['badge', statusClass(row.item)]">{{ statusText(row.item) }}</span>
          </template>
          <template v-slot:cell(allowedTo)="row">
            {{ row.item.allowedTo }} まで
          </template>
          <template v-slot:cell(actions)="row">
            <b-link class="text-danger" @click="removeAgent(row.item.id)">解除</b-link>
          </template>
        </b-table>

        <!-- 代行者の登録 -->
        <div class="addBox mt-4 px-3 py-3">
          <div class="bold mb-2">代行者を登録する</div>
          <div class="flex flexLeft flexWrap flexMiddle">
            <div class="addField mr-3 mb-2">
              <div class="small">姓</div>
              <b-form-input v-model="newAgent.sei" placeholder="例：事務"/>
            </div>
            <div class="addField mr-3 mb-2">
              <div class="small">名</div>
              <b-form-input v-model="newAgent.mei" placeholder="例：花子"/>
            </div>
            <div class="addFieldWide mr-3 mb-2">
              <div class="small">メールアドレス（代行者のログインID）</div>
              <b-form-input v-model="newAgent.email" placeholder="agent@example.com"/>
            </div>
            <div class="mb-2 align-self-end">
              <b-button variant="primary" class="bold" @click="register">登録する</b-button>
            </div>
          </div>
          <b-alert :show="!!errMsg" variant="danger" class="mt-2 mb-0">{{ errMsg }}</b-alert>
          <p class="text-muted small mt-2 mb-0">
            ※ログインできる期限は自動で {{ defaultAllowedTo }} までになります（変更が必要な場合は事務局へご連絡ください）。
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/modules/api';

export default {
  name: 'gasMyAgent',
  data() {
    return {
      header: [
        { key: 'no', label: 'No', thStyle: { width: '52px' } },
        { key: 'name', label: '氏名', thStyle: { width: '160px' } },
        { key: 'email', label: 'メールアドレス' },
        { key: 'allowedTo', label: 'ログイン期限', thStyle: { width: '170px' } },
        { key: 'status', label: '状態', thStyle: { width: '100px' } },
        { key: 'actions', label: '操作', thStyle: { width: '80px' } },
      ],
      agents: [],
      newAgent: { sei: '', mei: '', email: '' },
      errMsg: '',
    };
  },
  computed: {
    // 設定日から見た直近の3月31日（日本の年度末）
    defaultAllowedTo() {
      const now = new Date();
      const year = now.getMonth() + 1 <= 3 ? now.getFullYear() : now.getFullYear() + 1;
      return `${year}/03/31`;
    },
  },
  async created() {
    await this.fetchAgents();
  },
  methods: {
    async fetchAgents() {
      this.$store.dispatch('page/onLoading');
      const res = await api.send('/api/user/agents/list').catch(() => false);
      if (res !== false) {
        this.agents = (res.data.agents || []).map(a => ({
          id: a.agent_id,
          name: a.name,
          email: a.email,
          // 無期限（事務局が期限なしで作った場合）は空文字で返る
          allowedTo: a.allowed_to || '無期限',
          certified: a.is_certified,
          expired: a.expired,
        }));
      }
      this.$store.dispatch('page/offLoading');
    },
    // 422/500 応答を単一エラーメッセージへ整形
    toErrMsg(error, fallback) {
      const d = (error.response && error.response.data) || {};
      if (d.errMsg) {
        return d.errMsg;
      }
      if (d.errs) {
        const msgs = Object.keys(d.errs).reduce((acc, k) => acc.concat(d.errs[k]), []);
        if (msgs.length > 0) {
          return msgs.join(' ');
        }
      }
      if (d.msg) {
        return d.msg;
      }
      return fallback;
    },
    statusText(item) {
      if (item.expired) {
        return '期限切れ';
      }
      return item.certified ? '利用中' : '招待中';
    },
    statusClass(item) {
      if (item.expired) {
        return 'badgeExpired';
      }
      return item.certified ? 'badgeActive' : 'badgePending';
    },
    async register() {
      this.errMsg = '';
      const sei = this.newAgent.sei.trim();
      const mei = this.newAgent.mei.trim();
      const email = this.newAgent.email.trim();
      if (!sei || !mei) {
        this.errMsg = '姓・名を入力してください。';
        return;
      }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        this.errMsg = '正しいメールアドレスを入力してください。';
        return;
      }
      // 2人目以降は確認する（複数の代行者が同じ手続きを編集できるため）
      if (this.agents.length > 0) {
        const msg = '既に代行者が登録されています。\n複数の代行者が同じ手続きを編集できるようになります。\nこのまま登録してよろしいですか？';
        if (!await this.confirm(msg)) {
          return;
        }
      }
      this.$store.dispatch('page/onWaiting');
      const res = await api.send('/api/user/agents/store', { sei, mei, mail_address: email })
        .catch((error) => {
          this.errMsg = this.toErrMsg(error, '登録に失敗しました。');
          return false;
        });
      this.$store.dispatch('page/offWaiting');
      if (res === false) {
        return;
      }
      this.newAgent = { sei: '', mei: '', email: '' };
      await this.fetchAgents();
      await this.alert('代行者を登録しました。ご本人にパスワード設定のメールを送信しました。', false);
    },
    async removeAgent(id) {
      if (!await this.confirm('この代行者の登録を解除します。\n解除すると、この方はログインできなくなります。\nよろしいですか？')) {
        return;
      }
      this.$store.dispatch('page/onWaiting');
      const res = await api.send(`/api/user/agents/destroy/${id}`).catch((error) => {
        this.errMsg = this.toErrMsg(error, '解除に失敗しました。');
        return false;
      });
      this.$store.dispatch('page/offWaiting');
      if (res === false) {
        return;
      }
      await this.fetchAgents();
    },
  },
};
</script>

<style scoped>
/* 既存の設定画面（MyAuth.vue）と同じ見出し・余白の作法にそろえる */
.title {
  color: #333333;
  font-weight: 300;
  font-size: 24px;
  margin: 0 100px;
  margin-top: 52px;
  border-bottom: solid 2px #A0A9D0;
  padding-left: 10px;
  padding-bottom: 7px;
}
.inputBox {
  margin: 40px 100px 80px;
}
.lead {
  font-size: 15px;
}
.notes {
  font-size: 13px;
  color: #555;
  padding-left: 20px;
}
.addBox {
  background: #F4FAFB;
  border: 1px solid #D5EAF0;
  border-radius: 4px;
}
.addField {
  width: 150px;
}
.addFieldWide {
  width: 300px;
}
.flexWrap {
  flex-wrap: wrap;
}
.badgeActive {
  background: #0A8EA7;
  color: #fff;
}
.badgePending {
  background: #f0ad4e;
  color: #fff;
}
.badgeExpired {
  background: #999;
  color: #fff;
}
</style>
